import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule } from '@porsche-design-system/components-angular';
import {
  BehaviorSubject,
  distinctUntilChanged,
  type Observable,
  ReplaySubject,
  shareReplay,
  tap,
} from 'rxjs';
import type { RemoteUserLocation } from '../../shared/models/api/remote-user-location.model';
import { RemoteUserLocationService } from '../../shared/services/api/remote-user-location.service';
import { MediaQueryService } from '../../shared/services/media-query.service';
import { RemoteUserLocationCreateComponent } from './remote-user-location-create/remote-user-location-create.component';
import { RemoteUserLocationDeleteComponent } from './remote-user-location-delete/remote-user-location-delete.component';
import { RemoteUserLocationEditComponent } from './remote-user-location-edit/remote-user-location-edit.component';

@Component({
  selector: 'opa-remote-user-location-list',
  imports: [
    PorscheDesignSystemModule,
    CommonModule,
    LetDirective,
    RemoteUserLocationCreateComponent,
    RemoteUserLocationDeleteComponent,
    RemoteUserLocationEditComponent,
  ],
  templateUrl: './remote-user-location-list.component.html',
  styleUrl: './remote-user-location-list.component.scss',
})
export class RemoteUserLocationListComponent {
  readonly mediaQueryService = inject(MediaQueryService);
  private readonly remoteUserLocationService = inject(RemoteUserLocationService);

  private readonly loading = new BehaviorSubject(false);
  private readonly remoteUserLocation = new ReplaySubject<RemoteUserLocation | null>(1);
  readonly mediaMaxXs$ = this.mediaQueryService.max('xs');
  readonly loading$ = this.loading.pipe(distinctUntilChanged());
  readonly remoteUserLocation$ = this.remoteUserLocation.asObservable();

  data$ = this.getData$();
  showCreateModal = false;
  showDeleteModal = false;
  showEditModal = false;

  trackRemoteUserLocation(index: number, remoteUserLocation: RemoteUserLocation): number {
    return remoteUserLocation.id;
  }

  reloadData(): void {
    this.data$ = this.getData$();
  }

  private getData$(): Observable<RemoteUserLocation[]> {
    this.loading.next(true);

    return this.remoteUserLocationService.getRemoteUserLocations().pipe(
      tap({
        next: () => this.loading.next(false),
        error: () => this.loading.next(false),
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  openDeleteModal(remoteUserLocation: RemoteUserLocation): void {
    this.remoteUserLocation.next(remoteUserLocation);
    this.showDeleteModal = true;
  }

  closeDeleteModal(remoteUserLocation?: RemoteUserLocation): void {
    this.showDeleteModal = false;
    if (remoteUserLocation) this.reloadData();
    this.remoteUserLocation.next(null);
  }

  openEditModal(remoteUserLocation: RemoteUserLocation): void {
    this.remoteUserLocation.next(remoteUserLocation);
    this.showEditModal = true;
  }

  closeEditModal(success?: boolean): void {
    this.showEditModal = false;
    if (success) this.reloadData();
    this.remoteUserLocation.next(null);
  }
}
